import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, switchMap, throwError } from 'rxjs';

import { SKIP_SESSION_REFRESH } from '../../api';
import { AuthApiService } from '../../auth/auth-api.service';
import {
  CRSF_EXCLUDED_ENDPOINTS,
  CSRF_HEADER_NAME,
  SESSION_REFRESH_DISABLED_VALUE,
  SESSION_REFRESH_HEADER_NAME,
  SignInQueryParam,
  SignInReason,
} from '../../auth/auth.constant';
import { AuthService } from '../../auth/auth.service';
import { AuthUtility } from '../../auth/auth.utility';
import { HTTP_ERROR_HANDLED } from '../../auth/http-context-tokens.constant';
import { environment } from '../../environments/environment';
import { RouteFragment } from '../../shared/common';

import { CrsfStorageService } from './crsf-storage.service';

export function generalAuthInterceptorFn(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const authService = inject(AuthService);
  const router = inject(Router);
  const isApiRequest = isApiUrl(req.url);

  if (!isApiRequest) {
    return next(req);
  }

  return next(getAuthenticatedReq(req)).pipe(
    catchError((error: unknown) => {
      const isHandledByCaller = req.context.get(HTTP_ERROR_HANDLED);
      const shouldClearAuth = AuthUtility.isSessionLostError(error) && !isHandledByCaller;

      if (shouldClearAuth) {
        authService.logout();
        void router.navigate(['/', RouteFragment.SignIn], {
          queryParams: { [SignInQueryParam.Reason]: SignInReason.SessionExpired },
          replaceUrl: true,
        });
      }

      return throwError(() => error);
    }),
  );
}

export function crsfInterceptorFn(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  const authApiService = inject(AuthApiService);
  const crsfStorageService = inject(CrsfStorageService);
  const shouldSkipCrsfHandling = AuthUtility.isSessionManagerMode()
    || !isApiUrl(req.url)
    || isCrsfExcludedRequest(req.url);

  if (shouldSkipCrsfHandling) {
    return next(req);
  }

  const crsfToken = crsfStorageService.getCrsfToken();

  if (crsfToken.length) {
    const patchedReq = getReqWithCrsfHeader(req, crsfToken);
    return next(patchedReq);
  }

  return authApiService.getCsrfToken().pipe(
    switchMap((crsfTokenData) => {
      crsfStorageService.setCrsfTokenData(crsfTokenData);
      const patchedReq = getReqWithCrsfHeader(req, crsfTokenData.csrfToken);

      return next(patchedReq);
    }),
  );
}

function getAuthenticatedReq(req: HttpRequest<unknown>): HttpRequest<unknown> {
  const shouldSkipSessionRefresh = AuthUtility.isSessionManagerMode()
    && req.context.get(SKIP_SESSION_REFRESH);

  if (!shouldSkipSessionRefresh) {
    return req.clone({ withCredentials: true });
  }

  return req.clone({
    withCredentials: true,
    setHeaders: {
      [SESSION_REFRESH_HEADER_NAME]: SESSION_REFRESH_DISABLED_VALUE,
    },
  });
}

function isApiUrl(url: string): boolean {
  const usesSameOriginApi = environment.apiHost.length === 0;

  return usesSameOriginApi
    ? url.startsWith('/')
    : url.startsWith(environment.apiHost);
}

function getReqWithCrsfHeader(req: HttpRequest<unknown>, crsfToken: string): HttpRequest<unknown> {
  return req.clone({
    setHeaders: {
      [CSRF_HEADER_NAME]: crsfToken,
    },
  });
}

function isCrsfExcludedRequest(url: string): boolean {
  const { apiHost } = environment;
  const path = apiHost.length && url.startsWith(apiHost)
    ? url.slice(apiHost.length)
    : url;

  const normalizedUrlPath = normalizePath(path);

  return CRSF_EXCLUDED_ENDPOINTS.some(endpoint => endpoint === normalizedUrlPath);
}

function normalizePath(path: string): string {
  return path.startsWith('/')
    ? path.slice(1)
    : path;
}
